import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import type { ClientManagementState } from "../types";
import type { UseMutationResult } from "@tanstack/react-query";

interface DepartmentsTabProps {
  effectiveClientId: string | null;
  managementState: ClientManagementState;
  onChange: (field: keyof ClientManagementState, value: string | boolean) => void;
  createDepartmentMutation: UseMutationResult<unknown, unknown, { name: string; is_active: boolean }>;
  updateDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; payload: { name?: string; is_active?: boolean } }
  >;
  deleteDepartmentMutation: UseMutationResult<unknown, unknown, string>;
  departmentsLoading: boolean;
  departmentsList: { id: string; name: string; is_active?: boolean }[];
}

export const DepartmentsTab: React.FC<DepartmentsTabProps> = ({
  effectiveClientId,
  managementState,
  onChange,
  createDepartmentMutation,
  updateDepartmentMutation,
  deleteDepartmentMutation,
  departmentsLoading,
  departmentsList,
}) => {
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addActive, setAddActive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editActive, setEditActive] = useState(true);

  const filtered = departmentsList.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenAdd = () => {
    setAddName("");
    setAddActive(true);
    setAddOpen(true);
  };

  const handleConfirmAdd = () => {
    if (!addName.trim() || !effectiveClientId) return;
    createDepartmentMutation.mutate(
      { name: addName.trim(), is_active: addActive },
      { onSuccess: () => setAddOpen(false) }
    );
    // also sync managementState so parent stays consistent
    onChange("departmentName", addName.trim());
  };

  const handleStartEdit = (dept: { id: string; name: string; is_active?: boolean }) => {
    setEditingId(dept.id);
    setEditName(dept.name);
    setEditActive(dept.is_active !== false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleSaveEdit = (id: string) => {
    updateDepartmentMutation.mutate(
      { departmentId: id, payload: { name: editName, is_active: editActive } },
      { onSuccess: () => handleCancelEdit() }
    );
  };

  return (
    <SettingsSection>
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage departments.</Alert>
      )}

      <SectionCard
        title="Departments"
        description={`${departmentsList.length} department${departmentsList.length === 1 ? "" : "s"} configured`}
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            disabled={!effectiveClientId}
            onClick={handleOpenAdd}
          >
            Add department
          </Button>
        }
      >
        {/* Search bar */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search departments…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        <Divider />

        {/* List */}
        {departmentsLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading departments…</Typography>
          </Stack>
        ) : departmentsList.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <BusinessIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">No departments yet.</Typography>
            <Typography variant="caption">Click "Add department" to create one.</Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No departments match "{search}"
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {filtered.map((dept) => {
              const isEditing = editingId === dept.id;
              const isActive = dept.is_active !== false;

              return (
                <Box key={dept.id} sx={{ py: 1.25 }}>
                  {isEditing ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editName.trim()) handleSaveEdit(dept.id);
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Chip
                        label={editActive ? "Active" : "Inactive"}
                        size="small"
                        color={editActive ? "success" : "default"}
                        variant={editActive ? "filled" : "outlined"}
                        onClick={() => setEditActive((v) => !v)}
                        sx={{ cursor: "pointer" }}
                      />
                      <Tooltip title="Save">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!editName.trim() || updateDepartmentMutation.isPending}
                            onClick={() => handleSaveEdit(dept.id)}
                          >
                            {updateDepartmentMutation.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {dept.name}
                      </Typography>
                      <Chip
                        label={isActive ? "Active" : "Inactive"}
                        size="small"
                        color={isActive ? "success" : "default"}
                        variant="outlined"
                      />
                      <Tooltip title="Rename">
                        <IconButton size="small" onClick={() => handleStartEdit(dept)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleteDepartmentMutation.isPending}
                          onClick={() => {
                            if (window.confirm("Delete this department? This cannot be undone.")) {
                              deleteDepartmentMutation.mutate(dept.id);
                            }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add department</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Department name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && addName.trim()) handleConfirmAdd();
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={addActive}
                  onChange={(e) => setAddActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!addName.trim() || createDepartmentMutation.isPending}
            onClick={handleConfirmAdd}
          >
            {createDepartmentMutation.isPending ? <CircularProgress size={18} /> : "Add department"}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};
